import { Alert, CircularProgress, Typography } from "@mui/material";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { useOAuthCallback } from "@/shared/domain/mcp/hooks/useOAuthCallback";

import { SettingsPageLayout } from "../SettingsPageLayout";

import styles from "./OAuthCallbackHandler.module.css";

/**
 * Handles the OAuth callback redirect after a user authorizes with an external service.
 *
 * The OAuth provider redirects here with: /settings/mcp-connections/callback?code=...&state=...
 * The server_id is recovered from the state token on the backend.
 *
 * On success, closes the popup window (if opened as popup) or shows a success message.
 */
export function OAuthCallbackHandler() {
  const [searchParams] = useSearchParams();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const code = searchParams.get("code");
  const state = searchParams.get("state");

  const callbackMutation = useOAuthCallback(orgId);

  const calledRef = React.useRef(false);
  const [status, setStatus] = React.useState<"loading" | "success" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(() => {
    if (calledRef.current) {
      return;
    }
    calledRef.current = true;

    if (!code || !state || !orgId) {
      setStatus("error");
      setErrorMessage(
        !code || !state
          ? "Missing authorization code or state parameter."
          : "No organization context. Please sign in first.",
      );
      return;
    }

    callbackMutation.mutate(
      { request: { code, state } },
      {
        onSuccess: () => {
          setStatus("success");
          if (window.opener) {
            (window.opener as Window).postMessage(
              { type: "mcp-oauth-success" },
              window.location.origin,
            );
            window.close();
          }
        },
        onError: (err) => {
          setStatus("error");
          setErrorMessage(String(err));
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state, orgId]);

  return (
    <SettingsPageLayout>
      {status === "loading" && (
        <div className={styles.loadingRow}>
          <CircularProgress size={20} />
          <Typography>Completing authorization...</Typography>
        </div>
      )}
      {status === "success" && (
        <Alert severity="success">
          Successfully connected! You can close this window.
        </Alert>
      )}
      {status === "error" && (
        <Alert severity="error">
          Authorization failed.{" "}
          {errorMessage ||
            "Please try again from the MCP Connections settings page."}
        </Alert>
      )}
    </SettingsPageLayout>
  );
}
